(() => {
  if (customElements.get('ob-cerdena-bf')) return;

  class ObCerdenaBlackFriday extends HTMLElement {
    static discountUsed(cart, titles) {
      let amount = 0;
      for (const item of cart.items || []) {
        for (const allocation of item.line_level_discount_allocations || []) {
          if (titles.includes(allocation.discount_application?.title)) {
            amount += Number(allocation.amount) || 0;
          }
        }
      }
      return Math.min(2000, Math.max(0, amount));
    }

    static priceSummary(basePrice, selections, discountRemaining, eligible) {
      const accessories = selections.filter(Boolean);
      const subtotal = basePrice + accessories.reduce((sum, item) => sum + item.price, 0);
      const discount = eligible ? Math.min(accessories.length * 1000, discountRemaining, 2000) : 0;
      return { subtotal, discount, total: subtotal - discount };
    }

    static cartIdentity(cart, excludeId) {
      const byKey = new Map((cart.items || []).map(item => [item.key, item]));
      const items = (cart.items || []).filter(item => !excludeId || item.properties?._ob_cerdena_configuration !== excludeId).map(item => JSON.stringify([String(item.variant_id), item.quantity, Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)), String(item.selling_plan_allocation?.selling_plan?.id || ''), item.parent_relationship ? String(byKey.get(item.parent_relationship.parent_key)?.variant_id || item.parent_relationship.parent_key) : null])).sort();
      return JSON.stringify([cart.currency, items, (cart.discount_codes || []).map(code => code.code).sort()]);
    }

    static campaignDiscount(cart, titles) {
      return (cart.items || []).reduce((sum, item) => sum + (item.line_level_discount_allocations || []).reduce((amount, allocation) => amount + (titles.includes(allocation.discount_application?.title) ? Number(allocation.amount) || 0 : 0), 0), 0);
    }

    static confirmedIncrement(before, after, lines, titles) {
      const id = lines[0]?.properties?._ob_cerdena_configuration;
      if (!id || ObCerdenaBlackFriday.cartIdentity(before) !== ObCerdenaBlackFriday.cartIdentity(after, id) || !Number.isSafeInteger(before.total_price) || !Number.isSafeInteger(after.total_price)) throw new Error('Cart changed during confirmation');
      const subtotal = lines.reduce((sum, line) => sum + Number(line.original_line_price), 0);
      const discount = Math.max(0, ObCerdenaBlackFriday.campaignDiscount(after, titles) - ObCerdenaBlackFriday.campaignDiscount(before, titles));
      const total = subtotal - discount;
      if (!Number.isSafeInteger(subtotal) || !Number.isSafeInteger(total) || total < 0) throw new Error('Unconfirmed cart increment');
      return { subtotal, discount, total, actualCartIncrement: after.total_price - before.total_price };
    }

    static buildItems(baseId, selections, configurationId) {
      const properties = { _ob_cerdena_configuration: configurationId, _ob_cerdena_offer: 'bf-cerdena60-libra' };
      return [
        { id: String(baseId), quantity: 1, properties },
        ...selections.filter(Boolean).map(item => ({
          id: String(item.id),
          parent_id: String(baseId),
          quantity: 1,
          properties: { _ob_cerdena_configuration: configurationId }
        }))
      ];
    }

    static defaultVariant(product, variants) {
      const available = (product?.variants || []).map(item => variants.get(String(item.id))).filter(item => item?.available);
      if (product?.kind === 'column') {
        return available.find(item => item.id === String(product.preferredVariantId)) ||
          available.find(item => /\bestepa\b/i.test(item.title)) || available[0];
      }
      return available[0];
    }

    connectedCallback() {
      if (this.controller) return;
      try {
        this.initialize();
      } catch {
        this.controller?.abort();
        this.controller = null;
        this.showInitializationError();
      }
    }

    showInitializationError() {
      this.cartReady = false;
      const error = this.querySelector('[data-cbf-initialization-error]');
      const checking = this.querySelector('[data-cbf-checking]');
      const button = this.querySelector('[data-cbf-submit]');
      if (error) error.hidden = false;
      if (checking) checking.hidden = true;
      if (button) button.disabled = true;
    }

    initialize() {
      this.data = JSON.parse(this.querySelector('[data-cbf-data]').textContent);
      this.form = this.querySelector('form[data-cbf-form]');
      if (!this.form || !this.validContext()) throw new Error('Invalid configurator context');
      this.texts = this.data.texts;
      this.products = new Map(this.data.products.map(product => [String(product.id), { ...product, id: String(product.id) }]));
      this.variants = new Map();
      for (const product of this.products.values()) {
        for (const variant of product.variants) {
          this.variants.set(String(variant.id), { ...variant, id: String(variant.id), productId: String(product.id), product, kind: product.kind });
        }
      }
      this.selected = { mirror: null, column: null };
      this.money = new Intl.NumberFormat(this.data.locale || 'es', { style: 'currency', currency: this.data.currency });
      this.exactDiscount = this.data.currency === 'EUR';
      this.quoteClient = !this.exactDiscount && window.OBBlackFridayQuote ? window.OBBlackFridayQuote.create() : null;
      this.quoteSequence = 0;
      this.quoteReady = false;
      this.quotePending = false;
      this.nativeSummary = null;
      this.cartReady = false;
      this.discountRemaining = 0;
      this.busy = false;
      this.done = false;
      this.uncertain = false;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.form.addEventListener('change', event => this.changed(event), { signal });
      this.form.addEventListener('submit', event => this.submit(event), { signal });
      this.querySelector('[data-cbf-recheck]').addEventListener('click', () => this.refreshCart(), { signal });
      this.querySelector('[data-cbf-view-scene]').addEventListener('click', () => {
        this.querySelector('[data-cbf-scene]').scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }, { signal });
      this.querySelectorAll('[data-cbf-scroll]').forEach(button => {
        button.addEventListener('click', () => {
          const rail = this.querySelector('[data-cbf-rail="' + button.dataset.kind + '"]');
          rail?.scrollBy({ left: Number(button.dataset.cbfScroll) * (getComputedStyle(rail).direction === 'rtl' ? -1 : 1) * rail.clientWidth * 0.8, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }, { signal });
      });
      this.render();
      this.refreshCart();
    }

    disconnectedCallback() {
      this.invalidateNativeQuote();
      this.quoteClient?.dispose().catch(() => {});
      this.controller?.abort();
      this.controller = null;
    }

    validContext() {
      return String(this.data.shopId) === '92747563352' &&
        'ES,AD,CN,AE,US,ID,NO,GB,CH'.split(',').includes(this.data.country) &&
        ['EUR', 'CNY', 'AED', 'USD', 'IDR', 'GBP', 'CHF'].includes(this.data.currency) &&
        String(this.data.base?.id) === '59021582795096' &&
        this.data.base.sku === 'OB-BF-CER60-LIBRA' &&
        Number.isSafeInteger(this.data.base.price) && this.data.base.price > 0;
    }

    selectionList() {
      return [this.selected.mirror, this.selected.column].map(id => id ? this.variants.get(id) : null);
    }

    needsNativeQuote() {
      return !this.exactDiscount && this.data.discountsVerified && this.selectionList().some(Boolean);
    }

    invalidateNativeQuote() {
      clearTimeout(this.quoteTimer);
      this.quoteAbort?.abort();
      this.quoteSequence = (this.quoteSequence || 0) + 1;
      this.quoteReady = false;
      this.quotePending = false;
      this.nativeSummary = null;
    }

    queueNativeQuote() {
      this.invalidateNativeQuote();
      if (!this.needsNativeQuote() || !this.cartReady || this.done || this.uncertain) return;
      this.quotePending = true;
      this.quoteTimer = setTimeout(() => {
        this.requestNativeQuote().catch(error => {
          if (error.name === 'AbortError') return;
          this.showMessage(this.texts.preflightFailed);
          this.querySelector('[data-cbf-recheck]').hidden = false;
        }).finally(() => this.render());
      }, 300);
    }

    async requestNativeQuote(force = false) {
      this.invalidateNativeQuote();
      if (!this.needsNativeQuote()) return null;
      if (!this.quoteClient || !this.cartReady) throw new Error('Quote unavailable');
      const sequence = this.quoteSequence;
      const controller = new AbortController();
      this.quoteAbort = controller;
      this.quotePending = true;
      const base = this.data.base;
      const items = [{ id: String(base.id), quantity: 1, price: base.price }, ...this.selectionList().filter(Boolean).map(item => ({ id: String(item.id), quantity: 1, price: item.price, parent_id: String(base.id) }))];
      try {
        const result = await this.quoteClient.quote({ cart: this.currentCart, country: this.data.country, currency: this.data.currency, discountTitles: this.data.discountTitles, items, signal: controller.signal, force });
        if (sequence !== this.quoteSequence || controller.signal.aborted) throw new DOMException('Quote superseded', 'AbortError');
        const subtotal = items.reduce((sum, item) => sum + item.quantity * item.price, 0);
        if (result.currency !== this.data.currency || result.subtotal !== subtotal || !Number.isSafeInteger(result.discount) || result.discount < 0 || result.discount > subtotal || result.total !== subtotal - result.discount) throw new Error('Invalid quote');
        this.nativeSummary = result;
        this.quoteReady = true;
        return result;
      } catch (error) {
        if (sequence !== this.quoteSequence || controller.signal.aborted) throw new DOMException('Quote superseded', 'AbortError');
        throw error;
      } finally {
        if (sequence === this.quoteSequence) this.quotePending = false;
      }
    }

    format(amount) {
      return this.money.format(amount / 100);
    }

    changed(event) {
      if (this.busy || this.uncertain) return;
      const input = event.target;
      const kind = input.dataset.kind;
      if (!['mirror', 'column'].includes(kind)) return;
      if (input.hasAttribute('data-cbf-model')) {
        const product = this.products.get(input.value);
        const current = this.variants.get(this.selected[kind]);
        const variant = current?.productId === product?.id && current.available
          ? current
          : ObCerdenaBlackFriday.defaultVariant(product, this.variants);
        this.selected[kind] = variant ? variant.id : null;
      } else if (input.hasAttribute('data-cbf-variant')) {
        const variant = this.variants.get(input.value);
        if (!variant || variant.kind !== kind || !variant.available) return;
        this.selected[kind] = variant.id;
      } else {
        return;
      }
      this.done = false;
      this.confirmedSummary = null;
      this.querySelector('[data-cbf-confirmation]').hidden = true;
      this.clearMessage();
      this.queueNativeQuote();
      this.render();
    }

    showMessage(text, type = 'error') {
      const box = this.querySelector('[data-cbf-message]');
      box.hidden = false;
      box.dataset.state = type;
      box.querySelector('[data-cbf-message-text]').textContent = text;
      box.setAttribute('role', type === 'error' ? 'alert' : 'status');
    }

    clearMessage() {
      this.querySelector('[data-cbf-message]').hidden = true;
    }

    async readJSON(url) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Read failed');
        return await response.json();
      } finally {
        clearTimeout(timer);
      }
    }

    applyCartContext(cart) {
      if (cart.currency !== this.data.currency || !Array.isArray(cart.items)) throw new Error('Cart context changed');
      this.discountRemaining = this.exactDiscount ? Math.max(0, 2000 - ObCerdenaBlackFriday.discountUsed(cart, this.data.discountTitles)) : 0;
      this.cartReady = true;
      this.currentCart = cart;
    }

    async refreshCart() {
      if (this.busy || this.uncertain) return;
      this.querySelector('[data-cbf-recheck]').hidden = true;
      this.clearMessage();
      try {
        this.applyCartContext(await this.readJSON(this.data.cartJSON));
        this.queueNativeQuote();
      } catch {
        this.cartReady = false;
        this.invalidateNativeQuote();
        this.showMessage(this.texts.cartCheckFailed);
        this.querySelector('[data-cbf-recheck]').hidden = false;
      }
      this.render();
    }

    render() {
      const selections = this.selectionList();
      this.summary = this.done && this.confirmedSummary ? this.confirmedSummary : this.quoteReady && this.nativeSummary ? this.nativeSummary : ObCerdenaBlackFriday.priceSummary(this.data.base.price, selections, this.discountRemaining, this.data.discountsVerified && this.cartReady && this.exactDiscount);
      this.querySelectorAll('[data-cbf-base-price]').forEach(node => { node.textContent = this.format(this.data.base.price); });
      this.querySelectorAll('[data-cbf-total]').forEach(node => { node.textContent = this.format(this.summary.total); });
      this.querySelector('[data-cbf-total-label]').textContent = this.done || this.exactDiscount || this.quoteReady || !selections.some(Boolean) ? this.texts.total : this.texts.totalBeforeDiscount;
      this.querySelector('[data-cbf-subtotal]').textContent = this.format(this.summary.subtotal);
      this.querySelector('[data-cbf-discount]').textContent = '−' + this.format(this.summary.discount);
      this.querySelector('[data-cbf-discount-row]').hidden = this.summary.discount === 0;
      const canSaveOnAccessory = this.exactDiscount && this.data.discountsVerified && this.cartReady && this.discountRemaining >= 1000 && !this.done;
      this.querySelectorAll('[data-cbf-savings], [data-cbf-savings-note]').forEach(node => { node.hidden = !canSaveOnAccessory; });

      for (const kind of ['mirror', 'column']) {
        const variant = this.variants.get(this.selected[kind]);
        this.querySelectorAll('[data-cbf-model][data-kind="' + kind + '"]').forEach(input => {
          const product = this.products.get(input.value);
          input.checked = input.value === (variant?.productId || '');
          input.disabled = this.busy || this.uncertain || Boolean(product && !product.variants.some(entry => this.variants.get(String(entry.id))?.available));
        });
        this.querySelectorAll('[data-cbf-variant-group][data-kind="' + kind + '"]').forEach(group => {
          group.hidden = group.dataset.productId !== variant?.productId;
        });
        this.querySelectorAll('[data-cbf-variant][data-kind="' + kind + '"]').forEach(input => {
          const entry = this.variants.get(input.value);
          input.checked = input.value === variant?.id;
          input.disabled = this.busy || this.uncertain || !entry?.available;
        });
        const row = this.querySelector('[data-cbf-line="' + kind + '"]');
        row.hidden = !variant;
        if (variant) {
          row.querySelector('[data-cbf-line-name]').textContent = variant.product.title + (variant.title === 'Default Title' ? '' : ' · ' + variant.title);
          row.querySelector('[data-cbf-line-price]').textContent = this.format(variant.price);
          const image = row.querySelector('img');
          const source = variant.image || variant.product.image;
          if (source) image.src = source;
          else image.removeAttribute('src');
          image.alt = variant.product.title + (variant.title === 'Default Title' ? '' : ' · ' + variant.title);
          image.hidden = !image.getAttribute('src');
        }
      }

      const budgetNote = this.querySelector('[data-cbf-budget-note]');
      budgetNote.hidden = !this.exactDiscount || this.done || !this.data.discountsVerified || !this.cartReady || this.discountRemaining === 2000;
      budgetNote.textContent = this.discountRemaining === 0 ? this.texts.discountUsed : this.texts.discountPartlyUsed;
      const currencyNote = this.querySelector('[data-cbf-currency-note]');
      if (currencyNote) currencyNote.hidden = this.done || this.quoteReady;
      this.querySelector('[data-cbf-checking]').hidden = this.uncertain || (this.cartReady && !this.quotePending);
      const button = this.querySelector('[data-cbf-submit]');
      button.disabled = this.busy || this.uncertain || this.done || !this.cartReady || (this.needsNativeQuote() && !this.quoteReady) || !this.data.base.available || selections.filter(Boolean).some(item => !item.available);
      button.setAttribute('aria-busy', String(this.busy || this.quotePending));
      button.querySelector('[data-cbf-button-text]').textContent = this.busy ? this.texts.adding : this.done ? this.texts.addedButton : this.texts.addButton;
      this.form.setAttribute('aria-busy', String(this.busy || this.quotePending));
    }

    async preflight(selections) {
      const handles = [...new Set([this.data.base.handle, ...selections.filter(Boolean).map(item => item.product.handle)])];
      const [cart, ...products] = await Promise.all([
        this.readJSON(this.data.cartJSON),
        ...handles.map(handle => this.readJSON(this.data.rootURL.replace(/\/?$/, '/') + 'products/' + encodeURIComponent(handle) + '.js'))
      ]);
      const oldRemaining = this.discountRemaining;
      this.applyCartContext(cart);
      let changed = oldRemaining !== this.discountRemaining;
      for (let index = 0; index < products.length; index++) {
        const product = products[index];
        if (!Array.isArray(product.variants)) throw new Error('Missing variants');
        if (handles[index] === this.data.base.handle) {
          const live = product.variants.find(item => String(item.id) === String(this.data.base.id));
          if (!live || live.sku !== this.data.base.sku) throw new Error('Invalid base');
          if (!Number.isSafeInteger(live.price) || live.price <= 0) throw new Error('Invalid base price');
          changed ||= this.data.base.price !== live.price;
          this.data.base.price = live.price;
          this.data.base.available = live.available;
        }
        for (const item of selections.filter(Boolean).filter(entry => entry.product.handle === handles[index])) {
          const live = product.variants.find(entry => String(entry.id) === item.id);
          if (!live || (item.sku && live.sku !== item.sku)) {
            item.available = false;
          } else {
            if (!Number.isSafeInteger(live.price) || live.price <= 0) throw new Error('Invalid accessory price');
            changed ||= item.price !== live.price;
            item.price = live.price;
            item.available = live.available;
          }
        }
      }
      return changed;
    }

    async submit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy || this.done || this.uncertain || !this.cartReady || !this.validContext() || (this.needsNativeQuote() && !this.quoteReady)) return;
      const selections = this.selectionList();
      const displayedQuote = this.nativeSummary;
      this.clearMessage();
      this.busy = true;
      this.render();
      let posted = false;
      try {
        let changed = await this.preflight(selections);
        if (!this.exactDiscount) this.invalidateNativeQuote();
        if (!this.data.base.available || selections.filter(Boolean).some(item => !item.available)) {
          this.showMessage(this.texts.unavailable);
          return;
        }
        if (this.needsNativeQuote()) {
          const freshQuote = await this.requestNativeQuote(true);
          changed ||= !displayedQuote || displayedQuote.total !== freshQuote.total || displayedQuote.discount !== freshQuote.discount;
        }
        if (changed) {
          this.showMessage(this.texts.priceChanged, 'notice');
          return;
        }
        const id = window.crypto?.randomUUID ? window.crypto.randomUUID() : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
        const items = ObCerdenaBlackFriday.buildItems(this.data.base.id, selections, id);
        const expected = this.quoteReady ? this.nativeSummary.total : ObCerdenaBlackFriday.priceSummary(this.data.base.price, selections, this.discountRemaining, this.data.discountsVerified && this.exactDiscount).total;
        const beforeCart = this.currentCart;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 25000);
        let response;
        let result;
        const cartDrawer = document.querySelector('cart-drawer');
        const sections = typeof cartDrawer?.getSectionsToRender === 'function'
          ? cartDrawer.getSectionsToRender().map(section => section.id)
          : ['cart-icon-bubble'];
        cartDrawer?.setActiveElement?.(this.querySelector('[data-cbf-submit]'));
        posted = true;
        try {
          response = await fetch(this.data.addURL, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
            signal: controller.signal
          });
          result = await response.json();
        } finally {
          clearTimeout(timer);
        }
        if (!response.ok || result?.status) throw new Error('Cart mutation not confirmed');
        const cart = await this.readJSON(this.data.cartJSON);
        this.applyCartContext(cart);
        const lines = cart.items.filter(item => item.properties?._ob_cerdena_configuration === id);
        if (lines.length !== items.length || items.some(item => !lines.some(line => String(line.variant_id) === item.id && line.quantity === 1))) {
          throw new Error('Added lines not confirmed');
        }
        const parentLine = lines.find(line => String(line.variant_id) === String(this.data.base.id));
        if (lines.some(line => line !== parentLine && line.parent_relationship?.parent_key !== parentLine.key)) {
          throw new Error('Nested relationship not confirmed');
        }
        const actualTotal = lines.reduce((sum, line) => sum + Number(line.final_line_price), 0);
        if (!Number.isSafeInteger(actualTotal)) throw new Error('Added total not confirmed');
        const actualSubtotal = lines.reduce((sum, line) => sum + Number(line.original_line_price), 0);
        if (!Number.isSafeInteger(actualSubtotal) || actualSubtotal < actualTotal) throw new Error('Added subtotal not confirmed');
        this.confirmedSummary = { subtotal: actualSubtotal, discount: actualSubtotal - actualTotal, total: actualTotal };
        if (!this.exactDiscount) this.confirmedSummary = ObCerdenaBlackFriday.confirmedIncrement(beforeCart, cart, lines, this.data.discountTitles);
        this.done = true;
        const confirmation = this.querySelector('[data-cbf-confirmation]');
        confirmation.hidden = false;
        confirmation.querySelector('[data-cbf-confirmation-text]').textContent = this.texts.added + ' ' + this.format(this.confirmedSummary.total) + '.';
        const adjustment = confirmation.querySelector('[data-cbf-adjustment]');
        adjustment.hidden = this.confirmedSummary.total === expected && (this.confirmedSummary.actualCartIncrement === undefined || this.confirmedSummary.actualCartIncrement === this.confirmedSummary.total);
        adjustment.textContent = this.texts.finalDiscount;
        confirmation.focus({ preventScroll: true });
        this.quoteClient?.dispose().catch(() => {});
        this.quoteClient = !this.exactDiscount && window.OBBlackFridayQuote ? window.OBBlackFridayQuote.create() : null;
        document.dispatchEvent(new CustomEvent('ob:cerdena:added', { detail: { variantId: this.data.base.id, quantity: 1 } }));
        try {
          if (typeof cartDrawer?.renderContents === 'function' && sections.every(section => result.sections?.[section])) {
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents(result);
          } else {
            const cartIcon = document.querySelector('#cart-icon-bubble');
            if (cartIcon && result.sections?.['cart-icon-bubble']) {
              const rendered = new DOMParser().parseFromString(result.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
              if (rendered) cartIcon.innerHTML = rendered.innerHTML;
            }
          }
        } catch {
          // The confirmed cart remains accessible through the success link.
        }
      } catch {
        if (posted) {
          this.uncertain = true;
          this.showMessage(this.texts.unconfirmed);
        } else {
          this.showMessage(this.texts.preflightFailed);
          this.querySelector('[data-cbf-recheck]').hidden = false;
        }
      } finally {
        this.busy = false;
        this.render();
      }
    }
  }

  customElements.define('ob-cerdena-bf', ObCerdenaBlackFriday);
})();
